import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
  Autocomplete,
  FormControlLabel,
  Switch,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

import {
  PlatformMapping,
  useGetPlatformMappingsQuery,
  useUpdateAssociatedCampaignMutation,
} from "../../store/apis/briefingApi";
import { setToastMessage } from "../../store/slices/app/appSlice";
import { components } from "../../types/api";
import { useGetPlatformCandidatesMutation } from "../../store/apis/briefingApi";
import { useUserRole } from "../../hooks/useUserRole";
import { CandidateSelector } from "./CandidateSelector";

type BriefingPlatform = components["schemas"]["BriefingPlatform"];
type BriefingCampaignType = components["schemas"]["BriefingCampaignType"];
type EntityType = components["schemas"]["EntityType"];

interface EditAcInfoDialogProps {
  open: boolean;
  briefingId: string;
  associatedId: string;
  briefing?: components["schemas"]["BriefingDetailResponse"];
  initialName: string;
  initialSourceId: string;
  initialCampaignType?: BriefingCampaignType | null;
  platform: BriefingPlatform;
  campaignLevel: EntityType;
  companyId?: string;
  initialNativeAdProvider?: "TABOOLA" | "OUTBRAIN" | null;
  initialInvestmentPayment?: "CLIENTE" | "TI_MEDIA_GROUP_SL" | null;
  initialSubcampaignName?: string | null;
  initialAdvertiserId?: string | null;
  initialVisibleToClients?: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const EditAcInfoDialog: React.FC<EditAcInfoDialogProps> = ({
  open,
  briefingId,
  associatedId,
  briefing,
  initialName,
  initialSourceId,
  initialCampaignType,
  platform: initialPlatform,
  campaignLevel: initialCampaignLevel,
  companyId,
  initialNativeAdProvider,
  initialInvestmentPayment,
  initialSubcampaignName,
  initialAdvertiserId,
  initialVisibleToClients,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { role, isClient } = useUserRole();
  const canSetInvestment =
    role === "ADMIN" || role === "ACCOUNT" || role === "PRICING";

  // --- form state ---
  const [name, setName] = useState(initialName);
  const [selectedPlatform, setSelectedPlatform] =
    useState<BriefingPlatform>(initialPlatform);
  const [selectedCampaignType, setSelectedCampaignType] = useState<
    BriefingCampaignType | ""
  >(initialCampaignType ?? "");

  const initialFunnelExtracted = useMemo(() => {
    const parts = initialName.split("_");
    const lastPart = parts[parts.length - 1];
    return ["AWARENESS", "CONSIDERATION", "PERFORMANCE"].includes(lastPart)
      ? (lastPart as components["schemas"]["_36_Enums.BriefingFunnel"])
      : "";
  }, [initialName]);

  const [funnel, setFunnel] = useState<
    components["schemas"]["_36_Enums.BriefingFunnel"] | ""
  >(initialFunnelExtracted);

  const [campaignLevel, setCampaignLevel] =
    useState<EntityType>(initialCampaignLevel);
  const [sourceId, setSourceId] = useState(initialSourceId);
  const [nativeAdProvider, setNativeAdProvider] = useState<
    "TABOOLA" | "OUTBRAIN" | null
  >(initialNativeAdProvider ?? null);
  const [investmentPayment, setInvestmentPayment] = useState<
    "CLIENTE" | "TI_MEDIA_GROUP_SL" | null
  >(initialInvestmentPayment ?? null);

  const [subcampaignName, setSubcampaignName] = useState(
    initialSubcampaignName ?? ""
  );
  const [advertiserId, setAdvertiserId] = useState(initialAdvertiserId ?? "");
  const [visibleToClients, setVisibleToClients] = useState(
    initialVisibleToClients ?? true
  );

  const [updateAssociatedCampaign, { isLoading: isSaving }] =
    useUpdateAssociatedCampaignMutation();

  // --- platform mappings (same as AddAssociatedCampaignDialog) ---
  const { data: platformMappingsData, isLoading: isLoadingMappings } =
    useGetPlatformMappingsQuery();
  const platformMappings: PlatformMapping[] = useMemo(
    () => platformMappingsData?.data ?? [],
    [platformMappingsData]
  );

  // All platforms available from mappings
  const availablePlatforms: BriefingPlatform[] = useMemo(
    () => platformMappings.map((m) => m.platform),
    [platformMappings]
  );

  // Campaign types for the currently selected platform
  const availableCampaignTypes: BriefingCampaignType[] = useMemo(
    () =>
      selectedPlatform
        ? (platformMappings.find((m) => m.platform === selectedPlatform)
            ?.campaignTypes ?? [])
        : [],
    [platformMappings, selectedPlatform]
  );

  const currentMapping = useMemo(
    () => platformMappings.find((m) => m.platform === selectedPlatform),
    [platformMappings, selectedPlatform]
  );

  // When platform changes: derive level from the backend mapping
  const handlePlatformChange = (newPlatform: BriefingPlatform) => {
    setSelectedPlatform(newPlatform);
    setSelectedCampaignType(""); // Reset campaign type (different platform may have different types)
    setSourceId(""); // Reset source ID (belongs to the old platform)
    const newMapping = platformMappings.find((m) => m.platform === newPlatform);
    setCampaignLevel(newMapping?.defaultCampaignLevel ?? "CAMPAIGN");
    setNativeAdProvider(null);
  };

  // --- native campaign picker ---
  const defaultCampaignLevel =
    currentMapping?.defaultCampaignLevel ?? "CAMPAIGN";
  const canFetchCampaigns = !!currentMapping && !!companyId;

  const [
    getPlatformCandidates,
    {
      data: platformCandidatesData,
      isLoading: isLoadingCandidates,
      error: candidatesError,
    },
  ] = useGetPlatformCandidatesMutation();

  useEffect(() => {
    if (open && selectedPlatform && briefingId) {
      getPlatformCandidates({
        id: briefingId,
        body: { platformType: selectedPlatform },
      });
    }
  }, [open, selectedPlatform, briefingId, getPlatformCandidates]);

  useEffect(() => {
    if (candidatesError) {
      dispatch(
        setToastMessage({
          message: t(
            "briefingsView.campaignFetchError",
            "Failed to load campaigns from platform. You can still enter the Source ID manually."
          ),
          severity: "error",
        })
      );
    }
  }, [candidatesError, dispatch, t]);

  // Show application-level errors returned inside the 200 response body
  useEffect(() => {
    const responseErrors = platformCandidatesData?.data?.errors;
    if (!responseErrors || responseErrors.length === 0) return;
    const messages = (
      responseErrors as Array<{ platform?: string; message?: string }>
    )
      .map(
        (e) =>
          `${e.platform ? `[${e.platform}] ` : ""}${e.message ?? "Unknown error"}`
      )
      .join("\n");
    dispatch(
      setToastMessage({
        message: messages,
        severity: "warning",
      })
    );
  }, [platformCandidatesData, dispatch]);

  const isSyncing = React.useRef(false);

  // --- sync when dialog opens ---
  useEffect(() => {
    if (open) {
      isSyncing.current = true;
      setName(initialName);
      setSelectedPlatform(initialPlatform);
      setSelectedCampaignType(initialCampaignType ?? "");
      setFunnel(initialFunnelExtracted);
      setCampaignLevel(initialCampaignLevel);
      setSourceId(initialSourceId);
      setNativeAdProvider(initialNativeAdProvider ?? null);
      setInvestmentPayment(initialInvestmentPayment ?? null);
      setSubcampaignName(initialSubcampaignName ?? "");
      setAdvertiserId(initialAdvertiserId ?? "");
      setVisibleToClients(initialVisibleToClients ?? true);

      // Let the synchronous effects finish, then mark sync done
      setTimeout(() => {
        isSyncing.current = false;
      }, 0);
    }
  }, [
    open,
    initialName,
    initialPlatform,
    initialCampaignType,
    initialCampaignLevel,
    initialSourceId,
    initialNativeAdProvider,
    initialInvestmentPayment,
    initialSubcampaignName,
    initialAdvertiserId,
    initialVisibleToClients,
    initialFunnelExtracted,
  ]);

  // Auto-fill Campaign Name based on briefing, product, platform, type, funnel
  useEffect(() => {
    if (!open || isSyncing.current || !briefing || !selectedPlatform) return;

    // During manual edit (not syncing), if subcampaignName hasn't changed from original, don't auto-update name to avoid wiping edits
    // FIX: Removing the strict guard because it prevents name generation from working when changing Platform or Campaign Type!
    // if (subcampaignName === (initialSubcampaignName ?? "")) return;

    const parts = [
      briefing.brandOrProduct,
      subcampaignName.trim() || undefined,
      briefing.campaignName,
      selectedPlatform,
      selectedCampaignType || undefined,
      funnel || undefined,
    ].filter(Boolean);
    setName(parts.join("_"));
  }, [
    selectedPlatform,
    selectedCampaignType,
    funnel,
    briefing,
    subcampaignName,
    open,
    initialSubcampaignName,
  ]);

  const handleSave = async () => {
    if (!name.trim()) return;
    if (selectedPlatform === "NATADS" && !nativeAdProvider) return;
    try {
      await updateAssociatedCampaign({
        briefingId,
        associatedId,
        body: {
          name: name.trim(),
          sourceId: sourceId.trim() || null,
          subcampaignName: subcampaignName.trim() || null,
          briefingCampaignType: selectedCampaignType || undefined,
          platform: selectedPlatform,
          campaignLevel,
          advertiserId:
            campaignLevel === "INSERTIONORDER"
              ? advertiserId.trim() || null
              : null,
          nativeAdProvider: nativeAdProvider ?? undefined,
          investmentPayment: investmentPayment ?? undefined,
          visibleToClients,
        },
      }).unwrap();
      dispatch(
        setToastMessage({
          message: t(
            "briefingsView.acUpdateSuccess",
            "Campaign updated successfully"
          ),
          severity: "success",
        })
      );
      onSuccess?.();
      onClose();
    } catch {
      dispatch(
        setToastMessage({
          message: t(
            "briefingsView.acUpdateError",
            "Failed to update campaign"
          ),
          severity: "error",
        })
      );
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: 700 }}>
        {t("briefingsView.editAcInfo", "Edit campaign info")}
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} pt={1}>
          {/* Name was moved down */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <FormControl fullWidth>
              <InputLabel>{t("briefingsView.platform", "Platform")}</InputLabel>
              <Select
                label={t("briefingsView.platform", "Platform")}
                value={selectedPlatform}
                onChange={(e) =>
                  handlePlatformChange(e.target.value as BriefingPlatform)
                }
                disabled={isLoadingMappings}
                autoFocus
              >
                {availablePlatforms.map((p) => (
                  <MenuItem key={p} value={p}>
                    {p}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {currentMapping && (
              <Tooltip
                title={
                  <Box sx={{ p: 0.5 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      {t("platformMappings.mappingDetails", "Mapping Details")}
                    </Typography>
                    <Typography variant="body2">
                      <strong>
                        {t("platformMappings.realPlatform", "Real Platform")}:
                      </strong>{" "}
                      {currentMapping.realPlatform || "-"}
                    </Typography>
                    <Typography variant="body2">
                      <strong>
                        {t("platformMappings.campaignTypes", "Types")}:
                      </strong>{" "}
                      {currentMapping.campaignTypes.join(", ")}
                    </Typography>
                  </Box>
                }
                arrow
                placement="right"
              >
                <IconButton size="small" color="info" sx={{ flexShrink: 0 }}>
                  <InfoOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>

          {/* Campaign type — filtered by platform from mappings */}
          <FormControl fullWidth>
            <InputLabel>
              {t("briefingsCreate.form.briefingCampaignType", "Campaign type")}
            </InputLabel>
            <Select
              label={t(
                "briefingsCreate.form.briefingCampaignType",
                "Campaign type"
              )}
              value={selectedCampaignType}
              onChange={(e) =>
                setSelectedCampaignType(
                  e.target.value as BriefingCampaignType | ""
                )
              }
              disabled={isLoadingMappings}
            >
              <MenuItem value="">
                <em>{t("common.none", "None")}</em>
              </MenuItem>
              {(availableCampaignTypes.length > 0
                ? availableCampaignTypes
                : []
              ).map((type) => (
                <MenuItem key={type} value={type}>
                  {t(
                    `briefingsCreate.form.briefingCampaignTypeOptions.${type}`,
                    type
                  )}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel>
              {t("briefingsView.goalForm.funnel", "Funnel")}
            </InputLabel>
            <Select
              label={t("briefingsView.goalForm.funnel", "Funnel")}
              value={funnel}
              onChange={(e) =>
                setFunnel(
                  e.target
                    .value as components["schemas"]["_36_Enums.BriefingFunnel"]
                )
              }
            >
              <MenuItem value="AWARENESS">AWARENESS</MenuItem>
              <MenuItem value="CONSIDERATION">CONSIDERATION</MenuItem>
              <MenuItem value="PERFORMANCE">PERFORMANCE</MenuItem>
            </Select>
          </FormControl>

          {/* Subcampaign Name */}
          <TextField
            label={t("briefingsCreate.form.subcampaignName", "Subcampaign")}
            value={subcampaignName}
            onChange={(e) => setSubcampaignName(e.target.value)}
            fullWidth
            placeholder={t(
              "briefingsView.subcampaignNamePlaceholder",
              "Optional — leave blank if not applicable"
            )}
          />

          {/* Name */}
          <TextField
            label={t("common.name", "Name")}
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            required
            error={!name.trim()}
            helperText={
              !name.trim()
                ? t("briefingsView.manualNameRequired", "Name is required.")
                : undefined
            }
          />

          {/* Level — auto-set from platform, but overridable */}
          <FormControl fullWidth>
            <InputLabel>{t("briefingsView.level", "Level")}</InputLabel>
            <Select
              label={t("briefingsView.level", "Level")}
              value={campaignLevel}
              onChange={(e) => setCampaignLevel(e.target.value as EntityType)}
            >
              <MenuItem value="CAMPAIGN">
                {t("briefingsView.campaign", "Campaign")}
              </MenuItem>
              <MenuItem value="INSERTIONORDER">
                {t("briefingsView.insertionOrder", "Insertion Order")}
              </MenuItem>
              <MenuItem value="LINEITEM">
                {t("briefingsView.lineItem", "Line Item")}
              </MenuItem>
            </Select>
          </FormControl>

          {campaignLevel === "INSERTIONORDER" && (
            <TextField
              label={t("briefingsView.advertiserId", "Advertiser ID")}
              value={advertiserId}
              onChange={(e) => setAdvertiserId(e.target.value)}
              fullWidth
              size="small"
              helperText={t(
                "briefingsView.advertiserIdHelp",
                "Optional. Defaults to the company's first DV360 advertiser ID."
              )}
            />
          )}

          {canSetInvestment && (
            <Autocomplete
              options={["CLIENTE", "TI_MEDIA_GROUP_SL"]}
              value={investmentPayment}
              onChange={(_e, val) =>
                setInvestmentPayment(
                  val as "CLIENTE" | "TI_MEDIA_GROUP_SL" | null
                )
              }
              getOptionLabel={(option) =>
                option === "CLIENTE"
                  ? t("briefingsView.paymentClient", "Client")
                  : t(
                      "briefingsView.paymentTiMediaGroupSl",
                      "TI MEDIA GROUP SL"
                    )
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t(
                    "briefingsView.investmentPayment",
                    "Investment Payment"
                  )}
                  size="small"
                />
              )}
              fullWidth
              size="small"
            />
          )}

          {selectedPlatform === "NATADS" && (
            <FormControl fullWidth error={!nativeAdProvider}>
              <InputLabel>
                {t("briefingsView.nativeAdProvider", "Native Ad Provider")}
              </InputLabel>
              <Select
                label={t(
                  "briefingsView.nativeAdProvider",
                  "Native Ad Provider"
                )}
                value={nativeAdProvider || ""}
                onChange={(e) =>
                  setNativeAdProvider(e.target.value as "TABOOLA" | "OUTBRAIN")
                }
                required
              >
                <MenuItem value="TABOOLA">
                  {t("briefingsView.taboola", "Taboola")}
                </MenuItem>
                <MenuItem value="OUTBRAIN">
                  {t("briefingsView.outbrain", "Outbrain")}
                </MenuItem>
              </Select>
            </FormControl>
          )}

          {!isClient && (
            <FormControlLabel
              control={
                <Switch
                  checked={visibleToClients}
                  onChange={(e) => setVisibleToClients(e.target.checked)}
                  color="success"
                />
              }
              label={t(
                "briefingsView.visibleToClients",
                "Visible para el cliente"
              )}
            />
          )}

          {/* Source ID */}
          <CandidateSelector
            selectedPlatform={selectedPlatform}
            defaultCampaignLevel={defaultCampaignLevel}
            sourceId={sourceId}
            setSourceId={setSourceId}
            setCampaignLevel={setCampaignLevel}
            setAdvertiserId={setAdvertiserId}
            setNativeAdProvider={setNativeAdProvider}
            canFetchCampaigns={canFetchCampaigns}
            isBusy={isLoadingCandidates}
            candidatesError={candidatesError}
            candidatesData={platformCandidatesData?.data}
            handleSave={handleSave}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSaving}>
          {t("common.cancel", "Cancel")}
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={
            isSaving ||
            !name.trim() ||
            (selectedPlatform === "NATADS" && !nativeAdProvider)
          }
          startIcon={
            isSaving ? (
              <CircularProgress size={16} color="inherit" />
            ) : undefined
          }
        >
          {t("common.save", "Save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditAcInfoDialog;
